let intendedDestinationRef = null;
let sessionExpiredHandler = null;
let sessionExpiryInProgress = false;

/**
 * True when a 401 means the session cookie/JWT is missing at the edge (APIM or SWA gate),
 * not an app JSON `{ error }` credential or permission failure.
 * @param {number} status
 * @param {string} bodyText
 */
export function isMissingSessionAuth401(status, bodyText = '') {
  if (status !== 401) return false;

  const trimmed = (bodyText || '').trim();
  if (!trimmed) return true;

  const lower = trimmed.toLowerCase();
  if (/jwt/.test(lower) && !trimmed.startsWith('{')) return true;
  if (lower.includes('staff access token')) return true;

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      // APIM validate-jwt: { statusCode: 401, message: "JWT not present." }
      const message =
        typeof parsed.message === 'string' ? parsed.message.toLowerCase() : '';
      if (message && /jwt/.test(message)) {
        return true;
      }
      // Backend app JSON `{ error: "..." }` — wrong password, invalid role, etc.
      if (typeof parsed.error === 'string') {
        return false;
      }
    }
  } catch {
    if (!trimmed.startsWith('{')) return true;
  }

  return false;
}

/** @param {() => void | Promise<void>} handler */
export function registerSessionExpiredHandler(handler) {
  sessionExpiredHandler = handler;
  return () => {
    if (sessionExpiredHandler === handler) {
      sessionExpiredHandler = null;
    }
  };
}

/** Clears client session and redirects to login when the user was signed in. */
export function notifySessionExpired() {
  if (sessionExpiryInProgress || !sessionExpiredHandler) return;
  sessionExpiryInProgress = true;
  Promise.resolve(sessionExpiredHandler())
    .then((handled) => {
      if (handled === false) sessionExpiryInProgress = false;
    })
    .catch((err) => {
      console.error('Session expiry handler failed:', err);
      sessionExpiryInProgress = false;
    });
}

/**
 * @param {string | { pathname?: string, search?: string }} [explicit]
 *   String path, React Router `location`, or omit to use `window.location`.
 * @returns {string} Path safe to store as `location.state.from` for post-login redirect.
 */
export function resolveReturnPathForLogin(explicit) {
  let path;
  if (typeof explicit === 'string' && explicit.startsWith('/') && !explicit.startsWith('//')) {
    path = explicit;
  } else if (explicit && typeof explicit.pathname === 'string') {
    const search = explicit.search ?? '';
    path = `${explicit.pathname}${search}`;
  } else if (typeof window !== 'undefined') {
    path = `${window.location.pathname}${window.location.search}`;
  } else {
    path = '/';
  }
  if (path === '/login' || path.startsWith('/login?')) {
    return '/';
  }
  const withoutTrailingSlash =
    path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  if (withoutTrailingSlash === '/launch') {
    return '/';
  }
  return path;
}

/**
 * Post-login (or already-authenticated) destination from React Router `location.state.from`.
 * @param {{ state?: { from?: string | { pathname?: string, search?: string } } }} [location]
 * @returns {string}
 */
export function getLoginReturnPathFromLocation(location) {
  const from = location?.state?.from;
  const rawFrom =
    typeof from === 'string'
      ? from
      : from?.pathname != null
        ? {
            pathname: from.pathname,
            search: from.search ?? '',
          }
        : undefined;
  return resolveReturnPathForLogin(rawFrom);
}

/** Capture the route the user was intentionally trying to reach next. */
export function setIntendedDestinationForLogin(path) {
  const normalized = resolveReturnPathForLogin(path);
  intendedDestinationRef = normalized === '/' ? null : normalized;
}

/** Options for `navigate('/login', …)` so SignIn can return user post-login. */
export function getNavigateToLoginOptions(explicitPath) {
  const targetPath = explicitPath ?? intendedDestinationRef;
  intendedDestinationRef = null;
  return {
    replace: true,
    state: {
      from: resolveReturnPathForLogin(targetPath),
    },
  };
}
